import {
	normalizeStatusValue,
	normalizedStatusEquals,
	normalizedStatusIn,
} from "le-coffre-resources/dist/Shared/statusUiCore";

export type AsyncStatusKind =
	| "idle"
	| "pending"
	| "ready"
	| "failed"
	| "done"
	| "active"
	| "inactive"
	| "archived"
	| "deleted"
	| "draft"
	| "published"
	| "requested"
	| "submitted"
	| "validated"
	| "rejected"
	| "cancelled"
	| "closed"
	| "in_progress"
	| "unknown";

export type AsyncStatusUiPayload = {
	raw: string;
	kind: AsyncStatusKind;
	isIdle: boolean;
	isPending: boolean;
	isReady: boolean;
	isFailed: boolean;
	isDone: boolean;
	isActive: boolean;
	isInactive: boolean;
	isArchived: boolean;
	isDeleted: boolean;
	isDraft: boolean;
	isPublished: boolean;
	isRequested: boolean;
	isSubmitted: boolean;
	isValidated: boolean;
	isRejected: boolean;
	isCancelled: boolean;
	isClosed: boolean;
	isInProgress: boolean;
	isUnknown: boolean;
	isTerminal: boolean;
	matches: (...statuses: string[]) => boolean;
};

export function normalizeStatus(rawStatus: string | null | undefined, fallback: string = "UNKNOWN"): string {
	return normalizeStatusValue(rawStatus, { fallback, caseMode: "upper" });
}

export function statusEquals(rawStatus: string | null | undefined, expected: string, fallback: string = "UNKNOWN"): boolean {
	return normalizedStatusEquals(rawStatus, expected, { fallback, caseMode: "upper" });
}

export function statusIn(
	rawStatus: string | null | undefined,
	expectedStatuses: readonly string[],
	fallback: string = "UNKNOWN",
): boolean {
	return normalizedStatusIn(rawStatus, expectedStatuses, { fallback, caseMode: "upper" });
}

function mapRawStatusToKind(raw: string): AsyncStatusKind {
	const kindByRaw: Record<string, AsyncStatusKind> = {
		IDLE: "idle",
		PENDING: "pending",
		READY: "ready",
		FAILED: "failed",
		ERROR: "failed",
		DONE: "done",
		ACTIVE: "active",
		INACTIVE: "inactive",
		ARCHIVED: "archived",
		DELETED: "deleted",
		DRAFT: "draft",
		PUBLISHED: "published",
		REQUESTED: "requested",
		SUBMITTED: "submitted",
		VALIDATED: "validated",
		REJECTED: "rejected",
		CANCELLED: "cancelled",
		CLOSED: "closed",
		CLOS: "closed",
		IN_PROGRESS: "in_progress",
	};
	return kindByRaw[raw] ?? "unknown";
}

type StatusFlags = Omit<AsyncStatusUiPayload, "raw" | "kind" | "matches">;

type StatusFlagKey = Exclude<keyof StatusFlags, "isTerminal" | "isUnknown">;

const STATUS_FLAG_BY_KIND: Partial<Record<AsyncStatusKind, StatusFlagKey>> = {
	idle: "isIdle",
	pending: "isPending",
	ready: "isReady",
	failed: "isFailed",
	done: "isDone",
	active: "isActive",
	inactive: "isInactive",
	archived: "isArchived",
	deleted: "isDeleted",
	draft: "isDraft",
	published: "isPublished",
	requested: "isRequested",
	submitted: "isSubmitted",
	validated: "isValidated",
	rejected: "isRejected",
	cancelled: "isCancelled",
	closed: "isClosed",
	in_progress: "isInProgress",
};

const TERMINAL_KINDS: ReadonlySet<AsyncStatusKind> = new Set([
	"done",
	"failed",
	"inactive",
	"archived",
	"deleted",
	"cancelled",
	"closed",
	"rejected",
	"validated",
]);

function buildStatusFlags(kind: AsyncStatusKind): StatusFlags {
	const flags: StatusFlags = {
		isIdle: false,
		isPending: false,
		isReady: false,
		isFailed: false,
		isDone: false,
		isActive: false,
		isInactive: false,
		isArchived: false,
		isDeleted: false,
		isDraft: false,
		isPublished: false,
		isRequested: false,
		isSubmitted: false,
		isValidated: false,
		isRejected: false,
		isCancelled: false,
		isClosed: false,
		isInProgress: false,
		isUnknown: kind === "unknown",
		isTerminal: TERMINAL_KINDS.has(kind),
	};
	const activeFlag = STATUS_FLAG_BY_KIND[kind];
	if (activeFlag) {
		flags[activeFlag] = true;
	}
	return flags;
}

export function buildStatusUiPayload(rawStatus: string | null | undefined, fallback: string = "UNKNOWN"): AsyncStatusUiPayload {
	const raw = normalizeStatus(rawStatus, fallback);
	const kind = mapRawStatusToKind(raw);
	return {
		raw,
		kind,
		...buildStatusFlags(kind),
		matches: (...statuses: string[]): boolean => statusIn(raw, statuses, fallback),
	};
}
